import { defaultSort } from '../../libs/filters/constants.js';
import CurrentPathFilter from '../../libs/resource-center/filters/current-path-filter.js';
import EditorFilter from '../../libs/resource-center/filters/editor-filter.js';
import ExcludedPathsFilter from '../../libs/resource-center/filters/excluded-paths-filter.js';
import PathFilter from '../../libs/resource-center/filters/path-filter.js';
import UserFilter from '../../libs/resource-center/filters/user-filter.js';
import ResourceCenter from '../../libs/resource-center/resource-center.js';
import { readBlockConfig } from '../../scripts/utils/aem-utils.js';
import { fetchAuthors } from '../../scripts/utils/author-utils.js';
import moveInstrumentation from '../../scripts/utils/editor-support-utils.js';
import {
  getIndexPath,
  INDEX_ARTICLES,
} from '../../scripts/utils/index-utils.js';
import {
  CONTENT_HUB_ID,
  CONTENT_HUB_TAGS_MAPPING,
  fetchTagList,
  getTagById,
  SAP_CUSTOMER_MODEL_ID,
} from '../../scripts/utils/tags-utils.js';
import { toTitleCase } from '../../scripts/utils/text-utils.js';
import getI18next from '../../scripts/utils/translations/get-translations.js';

/**
 * @param url {string}
 * @return {string}
 */
export function getPathFromUrl(url) {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.pathname;
  } catch (error) {
    return url;
  }
}

/**
 * @param startDate {Date | string}
 * @param endDate {Date | string}
 * @param id {string}
 * @return {{ id: string, label: string, value: string }[]}
 */
export function getMonthRange(startDate, endDate, id) {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const months = [];
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  while (start <= end) {
    const month = monthNames[start.getUTCMonth()];
    months.push({
      label: `${month} ${start.getUTCFullYear()}`,
      value: `${start.getUTCFullYear()}/${(start.getUTCMonth() + 1).toString().padStart(2, '0')}`,
      id,
    });
    start.setUTCMonth(start.getUTCMonth() + 1);
  }

  return months.reverse();
}

/*
  getMonthAmount: returns amount of months from the range
  range examples:
    - range = "month 8" -> 8
    - range = "20 year" -> 240
*/
/**
 * @param range {string}
 * @return {number}
 */
export function getMonthAmount(range) {
  let amount = parseInt(
    range.replace('year', '').replace('month', '').trim(),
    10,
  );
  if (range.includes('year')) {
    amount *= 12;
  }
  return amount;
}

/*
  rangeValue examples:
    - "2023-04-01 to 2024-11-01"
    - "8 Months"
    - "years: 1, months: 2"
*/
/**
 * @param rangeValue {string}
 * @param id {string}
 * @return {{ id: string, label: string, value: string }[]}
 */
export function getDateRange(rangeValue, id) {
  let startDate;
  let endDate;

  if (rangeValue.includes('to')) {
    const dates = rangeValue.split('to');
    startDate = dates[0].trim();
    endDate = dates[1].trim();
  } else {
    endDate = new Date();
    startDate = new Date(endDate);

    rangeValue
      .replaceAll(':', '')
      .replaceAll('s', '')
      .toLowerCase()
      .split(',')
      .map((value) => getMonthAmount(value))
      .filter((value) => value)
      .forEach((months) => {
        startDate.setUTCMonth(startDate.getUTCMonth() - months);
      });
  }

  return getMonthRange(startDate, endDate, id);
}

/**
 * @param urls {string | string[]}
 * @return { string | string[] }
 */
export function cleanupUrlsToPath(urls) {
  if (typeof urls === 'string') {
    return getPathFromUrl(urls) || urls;
  }
  return urls.map((url) => {
    const path = getPathFromUrl(url);
    if (path !== null) {
      return path;
    }
    return url;
  });
}

/**
 * @param block {HTMLElement}
 * @param tags {{[id: string]: {label: string, key: string}}}
 * @param id {string}
 * @param {object} i18next - i18next instance for translations
 * @return {{
 *   userConfig: {
 *     [id: string]: {
 *       id: string,
 *       name: string,
 *       items: {
 *         id: string,
 *         label: string,
 *         value: string,
 *         fullTitle: string
 *       }[]
 *     }
 *   },
 *   editorConfig: {[id: string]: string[]}
 * }}
 */
export function getFilterConfig(block, tags, id, i18next) {
  const configKeys = [
    'tags',
    'authors',
    'content-type',
    'limit',
    'info',
    'page-size',
    'sort',
    'excluded-articles',
  ];
  const editorConfig = {};
  const userConfig = {};
  const blockConfig = readBlockConfig(block);
  Object.entries(blockConfig).forEach(([blockKey, value]) => {
    const key = blockKey === 'cq-tags' ? 'tags' : blockKey;

    if (key === 'excluded-articles') {
      editorConfig[key] = cleanupUrlsToPath(value);
      return;
    }
    if (configKeys.includes(key)) {
      editorConfig[key] = value.split(',').map((v) => v.trim());
      return;
    }
    if (key === 'date-range') {
      const filterId = `${id}-date-range`;
      userConfig[filterId] = {
        id: filterId,
        name: 'Month/Year',
        items: getDateRange(value, filterId),
      };
      return;
    }
    if (key.endsWith('-label')) {
      return;
    }
    const labelKey = `${key}-label`;
    const label = blockConfig[labelKey];
    const filterId = `${id}-${key}`;
    const items = value
      .split(',')
      .flatMap((item) => {
        if (item.endsWith('/*')) {
          const contentHubItem = item.replace(/\/\*/, '');
          const customerModelItem =
            CONTENT_HUB_TAGS_MAPPING[contentHubItem] || contentHubItem;
          const contentHubPrefix = `${CONTENT_HUB_ID}:${contentHubItem}/`;
          const customerModelPrefix = `${SAP_CUSTOMER_MODEL_ID}:${customerModelItem}/`;

          return Object.entries(tags)
            .filter(
              ([tagKey]) =>
                tagKey.startsWith(contentHubPrefix) ||
                tagKey.startsWith(customerModelPrefix),
            )
            .reduce(
              (acc, [, tagValue]) => [
                ...acc,
                {
                  label: tagValue.label,
                  value: tagValue.key,
                  id: filterId,
                  fullTitle: tagValue.fullTitle,
                },
              ],
              [],
            );
        }
        const tag = getTagById(`${SAP_CUSTOMER_MODEL_ID}:${item.trim()}`, tags);
        return tag
          ? {
              label: tag.label,
              value: tag.key,
              id: filterId,
              fullTitle: tag.fullTitle,
            }
          : null;
      })
      .filter((item) => item !== null);
    userConfig[filterId] = {
      id: filterId,
      name: i18next.t(label, '') || i18next.t(key, toTitleCase(key)),
      items,
    };
  });
  return { editorConfig, userConfig };
}

export default async function decorateBlock(block) {
  const id = `rc-${[...document.querySelectorAll('.resource-center')].indexOf(block) + 1}`;
  const textOnly = block.classList.contains('text-only');
  const carousel = block.classList.contains('carousel');
  const horizontal = block.classList.contains('horizontal');
  const tags = await fetchTagList();
  const i18next = await getI18next();
  const { editorConfig, userConfig } = getFilterConfig(
    block,
    tags,
    id,
    i18next,
  );
  const pageSize = Number(editorConfig['page-size']) || 6;
  const authorIndex = fetchAuthors();
  const urlParams = new URLSearchParams(window.location.search);
  const initialSort =
    urlParams.get(`${id}-sort`) ||
    (editorConfig.sort && editorConfig.sort[0]) ||
    defaultSort;
  const page = +urlParams.get(`${id}-page`) || 1;
  const limit = editorConfig.limit ? editorConfig.limit[0] : null;
  const nonFilterParams = [
    `${id}-page`,
    `${id}-sort`,
    `${id}-order`,
    `${id}-limit`,
  ];
  const rcFilters = [
    new PathFilter(tags, null),
    new CurrentPathFilter(),
    new ExcludedPathsFilter(editorConfig['excluded-paths']),
    new EditorFilter(editorConfig, tags),
    new UserFilter(id, nonFilterParams, tags),
  ];
  const rc = new ResourceCenter({
    id,
    authorIndex,
    carousel,
    dataCacheKey: 'sapContentHubArticles',
    dataSource: getIndexPath(INDEX_ARTICLES),
    horizontal,
    filtersArray: rcFilters,
    infoType: editorConfig.info?.[0],
    limit,
    initialPage: page,
    nonFilterParams,
    pageSize,
    i18next,
    initialSort,
    tags,
    textOnly,
    userConfig,
  });
  const rcElement = rc.render();
  moveInstrumentation(block, rcElement);
  block.replaceWith(rcElement);
}
